/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useState, type ReactElement } from 'react';
import type { ShopperProducts } from '@/scapi';
import { NavLink } from '@/components/link';
import { useNavigate } from '@/hooks/use-navigate';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuTrigger,
    NavigationMenuViewport,
} from '@/components/ui/navigation-menu';
import { useSubCategoryMap } from '@/components/navigation-menu/context';
import { toNavTreeWithDeferred } from '@/lib/navigation/normalize';
import { SubnavPanel } from '@/components/navigation/subnav-panel';
import { FeaturedSubnav } from '@/components/navigation/featured-subnav';
import type { RenderNavContentSlot } from '@/lib/navigation/types';
import type { NavPreviewTarget } from '@/lib/navigation/preview-target';

type ScapiCategory = ShopperProducts.schemas['Category'];

function DesktopNavEntry({
    category,
    renderContentSlot,
    initialDrawerId,
}: {
    category: ScapiCategory;
    renderContentSlot?: RenderNavContentSlot;
    initialDrawerId?: string;
}): ReactElement {
    const navigate = useNavigate();
    const subCategories = useSubCategoryMap();
    const nav = toNavTreeWithDeferred(category, subCategories);
    const groups = nav.children;

    if (groups.length === 0) {
        return (
            <NavigationMenuItem>
                <NavLink to={nav.url} data-header-nav-item="l1">
                    {nav.name}
                </NavLink>
            </NavigationMenuItem>
        );
    }

    return (
        // Keyed by the category id, so a forced `value` (Builder preview) names this item.
        <NavigationMenuItem value={category.id}>
            <NavigationMenuTrigger
                data-header-nav-item="l1"
                // Mouse click navigates to the category; Enter/Space still opens the panel,
                // so keyboard users can explore subcategories instead of being sent away.
                // Touch has no hover: the first tap opens the panel (Radix, on click), a second
                // tap while this item's panel is open navigates. Radix's own `data-state` is the
                // open flag, so tapping outside or another L1 resets it with no state of ours.
                onPointerDown={(event) => {
                    if (
                        event.pointerType === 'mouse' ||
                        (event.pointerType === 'touch' && event.currentTarget.dataset.state === 'open')
                    ) {
                        void navigate(nav.url);
                    }
                }}>
                {nav.name}
            </NavigationMenuTrigger>
            <NavigationMenuContent className="!left-auto !right-auto !w-full !p-0 md:!w-full">
                {nav.featured ? (
                    <FeaturedSubnav
                        category={nav}
                        renderContentSlot={renderContentSlot}
                        initialDrawerId={initialDrawerId}
                    />
                ) : (
                    <SubnavPanel groups={groups} contentSlot={renderContentSlot?.(category)} />
                )}
            </NavigationMenuContent>
        </NavigationMenuItem>
    );
}

export function DesktopNav({
    categories,
    renderContentSlot,
    forcedOpen,
}: {
    categories: ScapiCategory[];
    /**
     * The subnav's content component: called with the open level 2 drawer on a featured category
     * (SCO-NAV-03 D), with the level 1 on a regular one (SCO-NAV-04).
     */
    renderContentSlot?: RenderNavContentSlot;
    /** Builder preview only: holds this panel (and drawer) open; pointer leave and Escape are ignored. */
    forcedOpen?: NavPreviewTarget;
}): ReactElement {
    // Radix owns which panel is open; mirroring it here is only so the scrim knows to show.
    const [mirroredCategory, setOpenCategory] = useState('');
    const openCategory = forcedOpen?.categoryId ?? mirroredCategory;

    return (
        // `viewport={false}` + an explicit viewport is the primitive's escape hatch for
        // positioning it; `data-viewport` still has to say true so Content keeps its styling.
        <NavigationMenu
            delayDuration={0}
            viewport={false}
            data-viewport
            value={openCategory}
            onValueChange={forcedOpen ? undefined : setOpenCategory}
            className="h-full max-w-none items-center">
            <NavigationMenuList>
                {categories.map((category) => (
                    <DesktopNavEntry
                        key={category.id}
                        category={category}
                        renderContentSlot={renderContentSlot}
                        initialDrawerId={category.id === forcedOpen?.categoryId ? forcedOpen.drawerId : undefined}
                    />
                ))}
            </NavigationMenuList>

            {openCategory ? (
                // SCO-NAV-03: the viewport below the panel is tinted while it is open —
                // Figma paints the frame behind it black at 50%. It must not take pointer
                // events: it sits inside the menu root, so capturing them would read as the
                // pointer never leaving, and the panel would stay open.
                <div
                    aria-hidden
                    className="pointer-events-none fixed inset-x-0 bottom-0 z-40 animate-in bg-black/50 fade-in duration-200 motion-reduce:animate-none"
                    style={{ top: 'var(--header-height)' }}
                />
            ) : null}

            <NavigationMenuViewport
                className="border-0 shadow-lg"
                style={{
                    // Anchored to both viewport edges rather than `100vw`, which would include
                    // the scrollbar gutter and overshoot the header by its width.
                    position: 'fixed',
                    top: 'var(--header-height)',
                    left: 0,
                    right: 0,
                    // The scroll container is the panel root (SubnavPanel, FeaturedSubnav), not
                    // this box: a bar here changes the size Radix measures, and it writes that
                    // back as a new height (SCO-483). `height` is bound to that measurement and
                    // Content is `md:absolute`, so it is the only source of height there is.
                    // Do not set a height here, and do not reserve a scrollbar gutter.
                }}
            />
        </NavigationMenu>
    );
}
